import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Save, Loader2, AlertCircle, Calendar, Users, X, Lock } from 'lucide-react';

import { useToast, ToastContainer } from '../common/Toast';
import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';
import VendorService from '../../services/vendorService';

import SourcingRfpService from '../../services/sourcingRFPService';

//////////////////////////////////////////////////////////////
// EMBEDDED STYLES
//////////////////////////////////////////////////////////////

const styles = {
  pageContainer: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '24px',
  },
  card: {
    background: 'white',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
  },
  cardBody: {
    padding: '32px',
  },
  formGroup: {
    marginBottom: '24px',
  },
  formLabel: {
    display: 'block',
    marginBottom: '8px',
    fontWeight: 500,
    fontSize: '14px',
    color: '#374151',
  },
  required: {
    color: '#dc2626',
    marginLeft: '4px',
  },
  formControl: {
    display: 'block',
    width: '100%',
    padding: '10px 14px',
    fontSize: '14px',
    lineHeight: '1.5',
    color: '#374151',
    backgroundColor: '#fff',
    border: '1px solid #d1d5db',
    borderRadius: '6px',
    transition: 'all 0.15s ease',
  },
  formControlFocus: {
    borderColor: '#2563eb',
    outline: 'none',
    boxShadow: '0 0 0 3px rgba(37, 99, 235, 0.1)',
  },
  formControlError: {
    borderColor: '#dc2626',
  },
  formControlDisabled: {
    backgroundColor: '#f9fafb',
    cursor: 'not-allowed',
    opacity: 0.6,
  },
  textarea: {
    resize: 'vertical',
    minHeight: '120px',
  },
  errorMessage: {
    display: 'flex',
    alignItems: 'center',
    marginTop: '6px',
    fontSize: '13px',
    color: '#dc2626',
  },
  infoBox: {
    display: 'flex',
    alignItems: 'flex-start',
    padding: '16px',
    backgroundColor: '#eff6ff',
    border: '1px solid #bfdbfe',
    borderRadius: '6px',
    fontSize: '14px',
    color: '#1e40af',
    marginTop: '24px',
  },
  warningBox: {
    display: 'flex',
    alignItems: 'flex-start',
    padding: '16px',
    backgroundColor: '#fffbeb',
    border: '1px solid #fbbf24',
    borderLeft: '4px solid #fbbf24',
    borderRadius: '6px',
    fontSize: '14px',
    color: '#92400e',
    marginBottom: '24px',
  },
  multiSelectContainer: {
    position: 'relative',
  },
  multiSelectTrigger: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    minHeight: '44px',
    padding: '8px 14px',
    backgroundColor: '#fff',
    border: '1px solid #d1d5db',
    borderRadius: '6px',
    cursor: 'pointer',
    transition: 'all 0.15s ease',
  },
  multiSelectTriggerDisabled: {
    backgroundColor: '#f9fafb',
    cursor: 'not-allowed',
    opacity: 0.6,
  },
  multiSelectTriggerError: {
    borderColor: '#dc2626',
  },
  multiSelectDropdown: {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    marginTop: '4px',
    backgroundColor: 'white',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
    zIndex: 1000,
    maxHeight: '300px',
    overflow: 'hidden',
  },
  searchBox: {
    padding: '12px',
    borderBottom: '1px solid #e5e7eb',
  },
  searchInput: {
    width: '100%',
    padding: '8px 12px',
    fontSize: '14px',
    border: '1px solid #d1d5db',
    borderRadius: '6px',
    outline: 'none',
  },
  optionsList: {
    maxHeight: '240px',
    overflowY: 'auto',
  },
  option: {
    display: 'flex',
    alignItems: 'center',
    padding: '10px 16px',
    cursor: 'pointer',
    transition: 'background-color 0.15s ease',
  },
  optionHover: {
    backgroundColor: '#f3f4f6',
  },
  checkbox: {
    width: '16px',
    height: '16px',
    marginRight: '10px',
    cursor: 'pointer',
  },
  badge: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '4px 10px',
    backgroundColor: '#2563eb',
    color: 'white',
    borderRadius: '6px',
    fontSize: '13px',
    fontWeight: 500,
    marginRight: '6px',
    marginBottom: '6px',
  },
  badgeRemove: {
    marginLeft: '6px',
    cursor: 'pointer',
    opacity: 0.8,
  },
  statusBadge: {
    display: 'inline-block',
    padding: '6px 12px',
    borderRadius: '6px',
    fontSize: '13px',
    fontWeight: 600,
  },
  placeholder: {
    color: '#9ca3af',
  },
  emptyState: {
    padding: '24px',
    textAlign: 'center',
    color: '#6b7280',
    fontSize: '14px',
  },
  backdrop: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 999,
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '48px 24px',
  },
  loadingText: {
    marginTop: '12px',
    color: '#6b7280',
    fontSize: '14px',
  },
  spinning: {
    animation: 'spin 1s linear infinite',
  },
};

// Add keyframes for spinning animation
const styleSheet = `
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

if (typeof document !== 'undefined') {
  const style = document.createElement('style');
  style.textContent = styleSheet;
  if (!document.head.querySelector('style[data-rfp-styles]')) {
    style.setAttribute('data-rfp-styles', 'true');
    document.head.appendChild(style);
  }
}

//////////////////////////////////////////////////////////////
// MULTI-SELECT DROPDOWN COMPONENT
//////////////////////////////////////////////////////////////

const MultiSelectDropdown = ({ 
  options = [], 
  selected = [], 
  onChange, 
  placeholder = 'Select vendors',
  label,
  required = false,
  error = null,
  disabled = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [hoveredIndex, setHoveredIndex] = useState(null);

  const filteredOptions = options.filter(opt => 
    opt.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const toggleOption = (optionId) => {
    if (disabled) return;
    
    if (selected.includes(optionId)) {
      onChange(selected.filter(id => id !== optionId));
    } else {
      onChange([...selected, optionId]);
    }
  };

  const removeVendor = (e, vendorId) => {
    e.stopPropagation();
    onChange(selected.filter(id => id !== vendorId));
  };

  const selectedVendors = options.filter(opt => selected.includes(opt._id));

  return (
    <div style={styles.formGroup}>
      {label && (
        <label style={styles.formLabel}>
          {label}
          {required && <span style={styles.required}>*</span>}
        </label>
      )}
      
      <div style={styles.multiSelectContainer}>
        <div
          style={{
            ...styles.multiSelectTrigger,
            ...(error ? styles.multiSelectTriggerError : {}),
            ...(disabled ? styles.multiSelectTriggerDisabled : {}),
          }}
          onClick={() => !disabled && setIsOpen(!isOpen)}
        >
          <div style={{ display: 'flex', flexWrap: 'wrap', flex: 1, gap: '4px' }}>
            {selectedVendors.length === 0 ? (
              <span style={styles.placeholder}>{placeholder}</span>
            ) : (
              selectedVendors.map(vendor => (
                <span key={vendor._id} style={styles.badge}>
                  {vendor.name}
                  {!disabled && (
                    <X 
                      size={14} 
                      style={styles.badgeRemove}
                      onClick={(e) => removeVendor(e, vendor._id)}
                    />
                  )}
                </span>
              ))
            )}
          </div>
          <Users size={16} style={{ color: '#9ca3af', marginLeft: '8px', flexShrink: 0 }} />
        </div>

        {isOpen && !disabled && (
          <div style={styles.multiSelectDropdown}>
            <div style={styles.searchBox}>
              <input
                type="text"
                style={styles.searchInput}
                placeholder="Search vendors..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onClick={(e) => e.stopPropagation()}
              />
            </div>
            
            <div style={styles.optionsList}>
              {filteredOptions.length === 0 ? (
                <div style={styles.emptyState}>
                  No vendors found
                </div>
              ) : (
                filteredOptions.map((option, index) => (
                  <div
                    key={option._id}
                    style={{
                      ...styles.option,
                      ...(hoveredIndex === index ? styles.optionHover : {}),
                    }}
                    onClick={() => toggleOption(option._id)}
                    onMouseEnter={() => setHoveredIndex(index)}
                    onMouseLeave={() => setHoveredIndex(null)}
                  >
                    <input
                      type="checkbox"
                      style={styles.checkbox}
                      checked={selected.includes(option._id)}
                      onChange={() => {}}
                    />
                    <span>{option.name}</span>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {error && (
          <div style={styles.errorMessage}>
            <AlertCircle size={14} style={{ marginRight: '4px' }} />
            {error}
          </div>
        )}
      </div>

      {/* Backdrop to close dropdown */}
      {isOpen && !disabled && (
        <div
          style={styles.backdrop}
          onClick={() => setIsOpen(false)}
        />
      )}
    </div>
  );
};

//////////////////////////////////////////////////////////////
// MAIN EDIT RFP COMPONENT
//////////////////////////////////////////////////////////////

const EditRfp = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { success, error: showError, toasts, removeToast } = useToast();

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    scope: '',
    invitedVendors: [],
    responseDeadline: '',
    status: 'DRAFT',
  });

  // UI state
  const [vendors, setVendors] = useState([]);
  const [loadingVendors, setLoadingVendors] = useState(false);
  const [loadingRfp, setLoadingRfp] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [focusedField, setFocusedField] = useState(null);
  const [isEditable, setIsEditable] = useState(true);

  //////////////////////////////////////////////////////////////
  // LOAD RFP & VENDORS
  //////////////////////////////////////////////////////////////

  useEffect(() => {
    loadRfp();
    loadVendors();
  }, [id]);

  const loadRfp = async () => {
  setLoadingRfp(true);
  try {
    const response = await SourcingRfpService.getRfpById(id);
    const rfp = response.data;

    const editable = rfp.status === 'DRAFT';
    setIsEditable(editable);

    setFormData({
      title: rfp.title || '',
      scope: rfp.scope || '',
      invitedVendors: rfp.invitedVendors || [],
      // Ensure lineItems is at least an empty array if null in DB
      lineItems: rfp.lineItems || [], 
      responseDeadline: rfp.responseDeadline 
        ? new Date(rfp.responseDeadline).toISOString().split('T')[0] 
        : '',
      status: rfp.status || 'DRAFT',
    });
  } catch (err) {
    showError(err.message || 'Failed to load RFP');
    navigate('/sourcing/rfps');
  } finally {
    setLoadingRfp(false);
  }
};

  const addLineItem = () => {
  setFormData(prev => ({
    ...prev,
    lineItems: [...prev.lineItems, { description: '', requirement: '' }]
  }));
};

const removeLineItem = (index) => {
  const updatedLines = formData.lineItems.filter((_, i) => i !== index);
  setFormData(prev => ({ ...prev, lineItems: updatedLines }));
};

const handleLineItemChange = (index, field, value) => {
  const updatedLines = [...formData.lineItems];
  updatedLines[index][field] = value;
  setFormData(prev => ({ ...prev, lineItems: updatedLines }));
};

  const loadVendors = async () => {
  setLoadingVendors(true);

  try {
    const response = await VendorService.getVendors({
      limit: 1000,
    });

    const vendorList =
      response?.data?.vendors ||
      response?.data ||
      response?.vendors ||
      [];

    setVendors(vendorList);

  } catch (err) {
    console.error("Vendor load error:", err);
    showError(err.message || 'Failed to load vendors');
  } finally {
    setLoadingVendors(false);
  }
};


  //////////////////////////////////////////////////////////////
  // FORM HANDLERS
  //////////////////////////////////////////////////////////////

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  //////////////////////////////////////////////////////////////
  // VALIDATION
  //////////////////////////////////////////////////////////////

  const validate = () => {
    const newErrors = {};

    if (!formData.title?.trim()) {
      newErrors.title = 'Title is required';
    }

    if (formData.invitedVendors.length === 0) {
      newErrors.invitedVendors = 'At least one vendor must be selected';
    }

    if (!formData.responseDeadline) {
      newErrors.responseDeadline = 'Response deadline is required';
    } else {
      const deadline = new Date(formData.responseDeadline);
      const now = new Date();
      now.setHours(0, 0, 0, 0);
      
      if (deadline < now) {
        newErrors.responseDeadline = 'Deadline must be in the future';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  //////////////////////////////////////////////////////////////
  // SUBMIT
  //////////////////////////////////////////////////////////////

  const handleSubmit = async (e) => {
  e.preventDefault();
  if (!isEditable) return;
  if (!validate()) return;

  setSubmitting(true);
  try {
    await SourcingRfpService.updateRfp(id, {
      title: formData.title.trim(),
      scope: formData.scope.trim(),
      invitedVendors: formData.invitedVendors,
      responseDeadline: formData.responseDeadline,
      lineItems: formData.lineItems, // <--- Include this
    });

    success('RFP updated successfully');
    navigate('/sourcing/rfps');
  } catch (err) {
    showError(err.message || 'Failed to update RFP');
  } finally {
    setSubmitting(false);
  }
};
  //////////////////////////////////////////////////////////////
  // CANCEL
  //////////////////////////////////////////////////////////////

  const handleCancel = () => {
    navigate('/sourcing/rfps');
  };

  //////////////////////////////////////////////////////////////
  // STATUS BADGE CONFIG
  //////////////////////////////////////////////////////////////

  const getStatusConfig = (status) => {
    const config = {
      DRAFT: { bg: '#f3f4f6', color: '#6b7280' },
      PUBLISHED: { bg: '#dbeafe', color: '#2563eb' },
      EVALUATION: { bg: '#fef3c7', color: '#d97706' },
      AWARDED: { bg: '#d1fae5', color: '#059669' },
      CANCELLED: { bg: '#fee2e2', color: '#dc2626' },
    };
    return config[status] || config.DRAFT;
  };

  //////////////////////////////////////////////////////////////
  // PAGE HEADER ACTIONS
  //////////////////////////////////////////////////////////////

  const headerActions = [
    {
      label: 'Cancel',
      variant: 'outline',
      onClick: handleCancel,
      disabled: submitting,
    },
    {
      label: submitting ? 'Saving...' : 'Save Changes',
      icon: submitting ? <Loader2 size={18} style={styles.spinning} /> : <Save size={18} />,
      variant: 'primary',
      onClick: handleSubmit,
      disabled: submitting || !isEditable,
    },
  ];

  //////////////////////////////////////////////////////////////
  // LOADING STATE
  //////////////////////////////////////////////////////////////

  if (loadingRfp) {
    return (
      <>
        <PageBreadcrumb />
        <PageHeader title="Edit RFP" description="Loading..." />
        <div style={styles.pageContainer}>
          <div style={styles.card}>
            <div style={{ ...styles.cardBody, textAlign: 'center', padding: '64px 32px' }}>
              <Loader2 size={48} style={{ ...styles.spinning, color: '#2563eb' }} />
              <div style={styles.loadingText}>Loading RFP...</div>
            </div>
          </div>
        </div>
      </>
    );
  }

  //////////////////////////////////////////////////////////////
  // RENDER
  //////////////////////////////////////////////////////////////

  const statusConfig = getStatusConfig(formData.status);

  return (
    <>
      <PageBreadcrumb />

      <PageHeader
        title="Edit RFP"
        description="Modify your Request for Proposal"
        actions={headerActions}
      />

      <div style={styles.pageContainer}>
        {/* Non-editable warning banner */}
        {!isEditable && (
          <div style={styles.warningBox}>
            <Lock size={20} style={{ marginRight: '12px', marginTop: '2px', flexShrink: 0 }} />
            <div>
              <strong>Editing Locked:</strong> This RFP can no longer be edited because it has been published. 
              Only draft RFPs can be modified.
            </div>
          </div>
        )}

        <div style={styles.card}>
          <div style={styles.cardBody}>
            <form onSubmit={handleSubmit}>
              {/* Title */}
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>
                  RFP Title
                  <span style={styles.required}>*</span>
                </label>
                <input
                  type="text"
                  style={{
                    ...styles.formControl,
                    ...(focusedField === 'title' ? styles.formControlFocus : {}),
                    ...(errors.title ? styles.formControlError : {}),
                    ...(submitting || !isEditable ? styles.formControlDisabled : {}),
                  }}
                  placeholder="Enter RFP title"
                  value={formData.title}
                  onChange={(e) => handleChange('title', e.target.value)}
                  onFocus={() => setFocusedField('title')}
                  onBlur={() => setFocusedField(null)}
                  disabled={submitting || !isEditable}
                />
                {errors.title && (
                  <div style={styles.errorMessage}>
                    <AlertCircle size={14} style={{ marginRight: '4px' }} />
                    {errors.title}
                  </div>
                )}
              </div>

              {/* Scope */}
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>Scope of Work</label>
                <textarea
                  style={{
                    ...styles.formControl,
                    ...styles.textarea,
                    ...(focusedField === 'scope' ? styles.formControlFocus : {}),
                    ...(submitting || !isEditable ? styles.formControlDisabled : {}),
                  }}
                  placeholder="Describe the scope of work, requirements, and deliverables..."
                  value={formData.scope}
                  onChange={(e) => handleChange('scope', e.target.value)}
                  onFocus={() => setFocusedField('scope')}
                  onBlur={() => setFocusedField(null)}
                  disabled={submitting || !isEditable}
                />
              </div>
              {/* Line Items Section */}
<div style={{ ...styles.formGroup, marginTop: '32px', borderTop: '1px solid #e5e7eb', paddingTop: '24px' }}>
  <label style={{ ...styles.formLabel, fontSize: '16px', fontWeight: '600' }}>
    Line Items & Requirements
  </label>
  <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '16px' }}>
    Specify the items or services you want vendors to bid on.
  </p>

  {formData.lineItems.map((item, index) => (
    <div key={index} style={{ 
      display: 'flex', 
      gap: '12px', 
      marginBottom: '12px', 
      alignItems: 'flex-start',
      background: '#f9fafb',
      padding: '12px',
      borderRadius: '8px'
    }}>
      <div style={{ flex: 2 }}>
        <input
          type="text"
          style={{...styles.formControl, ...( !isEditable ? styles.formControlDisabled : {})}}
          placeholder="Description (e.g. 50x Office Chairs)"
          value={item.description}
          onChange={(e) => handleLineItemChange(index, 'description', e.target.value)}
          disabled={!isEditable}
        />
      </div>
      <div style={{ flex: 1 }}>
        <input
          type="text"
          style={{...styles.formControl, ...( !isEditable ? styles.formControlDisabled : {})}}
          placeholder="Requirement/Qty"
          value={item.requirement}
          onChange={(e) => handleLineItemChange(index, 'requirement', e.target.value)}
          disabled={!isEditable}
        />
      </div>
      {isEditable && formData.lineItems.length > 0 && (
        <button
          type="button"
          onClick={() => removeLineItem(index)}
          style={{ padding: '8px', color: '#dc2626', background: 'none', border: 'none', cursor: 'pointer' }}
        >
          <X size={20} />
        </button>
      )}
    </div>
  ))}

  {isEditable && (
    <button
      type="button"
      onClick={addLineItem}
      style={{
        marginTop: '8px',
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
        color: '#2563eb',
        fontSize: '14px',
        fontWeight: '500',
        background: 'none',
        border: 'none',
        cursor: 'pointer'
      }}
    >
      + Add Item
    </button>
  )}
</div>
              {/* Invited Vendors */}
              {loadingVendors ? (
                <div style={styles.loadingContainer}>
                  <Loader2 size={32} style={{ ...styles.spinning, color: '#2563eb' }} />
                  <div style={styles.loadingText}>Loading vendors...</div>
                </div>
              ) : (
                <MultiSelectDropdown
                  label="Invited Vendors"
                  required
                  options={vendors}
                  selected={formData.invitedVendors}
                  onChange={(selected) => handleChange('invitedVendors', selected)}
                  placeholder="Select vendors to invite"
                  error={errors.invitedVendors}
                  disabled={!isEditable}
                />
              )}

              {/* Response Deadline */}
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>
                  Response Deadline
                  <span style={styles.required}>*</span>
                </label>
                <div style={{ position: 'relative' }}>
                  <input
                    type="date"
                    style={{
                      ...styles.formControl,
                      ...(focusedField === 'deadline' ? styles.formControlFocus : {}),
                      ...(errors.responseDeadline ? styles.formControlError : {}),
                      ...(submitting || !isEditable ? styles.formControlDisabled : {}),
                    }}
                    value={formData.responseDeadline}
                    onChange={(e) => handleChange('responseDeadline', e.target.value)}
                    onFocus={() => setFocusedField('deadline')}
                    onBlur={() => setFocusedField(null)}
                    disabled={submitting || !isEditable}
                    min={new Date().toISOString().split('T')[0]}
                  />
                  <Calendar 
                    size={16} 
                    style={{ 
                      position: 'absolute',
                      right: '14px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      color: '#9ca3af',
                      pointerEvents: 'none',
                    }}
                  />
                </div>
                {errors.responseDeadline && (
                  <div style={styles.errorMessage}>
                    <AlertCircle size={14} style={{ marginRight: '4px' }} />
                    {errors.responseDeadline}
                  </div>
                )}
              </div>

              {/* Status Badge */}
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>Status</label>
                <div>
                  <span
                    style={{
                      ...styles.statusBadge,
                      backgroundColor: statusConfig.bg,
                      color: statusConfig.color,
                    }}
                  >
                    {formData.status}
                  </span>
                </div>
              </div>

              {/* Info Box */}
              {isEditable && (
                <div style={styles.infoBox}>
                  <AlertCircle size={18} style={{ marginRight: '12px', marginTop: '2px', flexShrink: 0 }} />
                  <div>
                    Changes will be saved to the draft. You can publish this RFP from the RFP management page.
                  </div>
                </div>
              )}
            </form>
          </div>
        </div>
      </div>

      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </>
  );
};

export default EditRfp;